import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Chip, Button, Collapse, IconButton, Tooltip, Alert, Accordion, AccordionSummary, AccordionDetails, Divider } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  ExpandMore,
  ContentCopy,
  LocationOn,
  Business,
  Receipt,
  CheckCircle,
  Cancel,
  Warning,
  Visibility,
  VisibilityOff,
  Map
} from '@mui/icons-material';
import { formatForDisplay, formatDateOnly, formatTimeOnly } from '@utils/dateFormatter';

interface CompanyCompactCardProps {
  company: any;
  onCopy: (field: string, value: string) => void;
}

const CompanyCompactCard: React.FC<CompanyCompactCardProps> = ({ company, onCopy }) => {
  const [showSensitiveData, setShowSensitiveData] = useState(false);

  const formatRegCom = (regCom: string) => {
    if (!regCom) return '—';
    return regCom.replace(/(\d+)([A-Z]+)(\d+)/, '$1/$2/$3');
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    return formatDateOnly(dateStr);
  };

  const getTVAStatus = () => {
    if (company.platitorTVA) {
      return { status: 'DA', color: 'success' as const, icon: <CheckCircle /> };
    } else {
      return { status: 'NU', color: 'error' as const, icon: <Cancel /> };
    }
  };

  const getTVAIncasareStatus = () => {
    if (company.tvaLaIncasare) {
      return { status: 'DA', color: 'warning' as const, icon: <Warning /> };
    } else {
      return { status: 'NU', color: 'default' as const, icon: <CheckCircle /> };
    }
  };

  const getROEFacturaStatus = () => {
    if (company.roEFactura) {
      return { status: 'DA', color: 'success' as const, icon: <CheckCircle /> };
    } else {
      return { status: 'NU', color: 'default' as const, icon: <Cancel /> };
    }
  };

  const getFiscalStatus = () => {
    if (company.inactivFiscal) {
      return { status: 'INACTIV', color: 'error' as const, icon: <Cancel /> };
    } else if (company.status?.radiat) {
      return { status: 'RADIAT', color: 'error' as const, icon: <Cancel /> };
    } else {
      return { status: 'ACTIV', color: 'success' as const, icon: <CheckCircle /> };
    }
  };

  const tvaStatus = getTVAStatus();
  const tvaIncasareStatus = getTVAIncasareStatus();
  const roEFacturaStatus = getROEFacturaStatus();
  const fiscalStatus = getFiscalStatus();

  // Verificare probleme critice
  const isInactive = company.inactivFiscal;
  const isRadiated = company.status?.radiat;

  // Adresa pe o linie
  const getAddressLine = () => {
    if (company.adresaStructurata) {
      const { strada, numar, localitate, sector, judet, codPostal } = company.adresaStructurata;
      return `${strada || ''} ${numar || ''}, ${localitate || ''}${sector ? `, Sector ${sector}` : ''}, ${judet || ''}, ${codPostal || ''}`.replace(/,\s*,/g, ',').replace(/^,\s*|,\s*$/g, '');
    }
    return company.adresaDomiciliuFiscalText || '—';
  };

  const addressLine = getAddressLine();

  return (
    <Box>
      {/* Alerte pentru probleme critice */}
      {isInactive && (
        <Alert severity="error" sx={{ mb: 2 }}>
          ⚠️ Firma este inactiva fiscala - nu poti continua inregistrarea
        </Alert>
      )}
      
      {isRadiated && (
        <Alert severity="error" sx={{ mb: 2 }}>
          ⚠️ Firma este radiata - nu poti continua inregistrarea
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Card 1: Identitate companie */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Business color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Identitate companie
                </Typography>
              </Box>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  CUI: <strong>{company.CUI || '—'}</strong>
                  <Tooltip title="Copiaza CUI">
                    <IconButton size="small" onClick={() => onCopy('CUI', company.CUI || '')}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Typography>
              </Box>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Nr. Reg. Com.: <strong>{formatRegCom(company.nrInmatriculareRC)}</strong>
                </Typography>
              </Box>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Denumire: <strong>{company.denumireCompanie || '—'}</strong>
                </Typography>
              </Box>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Forma juridica: <strong>{company.formaJuridicaOrganizareProprietate || '—'}</strong>
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  CAEN: <strong>{company.codCAENPrincipal || '—'}</strong>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 2: Adresa */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOn color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Adresa
                </Typography>
              </Box>
              
              <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.4 }}>
                {addressLine}
              </Typography>
              
              <Button
                size="small"
                variant="outlined"
                startIcon={<Map />}
                onClick={() => window.open(`https://www.google.com/maps/search/${encodeURIComponent(addressLine)}`, '_blank')}
              >
                Deschide pe harta
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 3: Statut TVA */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Receipt color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Statut TVA
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={tvaStatus.icon}
                  label={`Platitor TVA: ${tvaStatus.status}`}
                  color={tvaStatus.color}
                  size="small"
                />
                <Chip
                  icon={tvaIncasareStatus.icon}
                  label={`TVA la incasare: ${tvaIncasareStatus.status}`}
                  color={tvaIncasareStatus.color}
                  size="small"
                />
              </Box>
              
              <Typography variant="caption" color="text.secondary">
                {tvaStatus.status === 'DA' ? 'Se va aplica cota TVA pe facturi' : 'Se va aplica cota 0% TVA pe facturi'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 4: Status fiscal */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Status fiscal
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={fiscalStatus.icon}
                  label={fiscalStatus.status}
                  color={fiscalStatus.color}
                  size="small"
                />
                <Chip
                  icon={roEFacturaStatus.icon}
                  label={`RO e-Factura: ${roEFacturaStatus.status}`}
                  color={roEFacturaStatus.color}
                  size="small"
                />
              </Box>
              
              <Typography variant="caption" color="text.secondary">
                {roEFacturaStatus.status === 'DA' ? 'Butonul "Trimite in RO e-Factura" va fi disponibil' : 'Butonul "Trimite in RO e-Factura" va fi ascuns'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Accordion pentru detalii suplimentare */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Detalii fiscale avansate
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {/* Istoric TVA */}
            {(company.dataInceputScpTVA || company.dataSfarsitScpTVA || company.dataInceputTvaInc || company.dataSfarsitTvaInc) && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Istoric TVA
                    </Typography>
                    {company.dataInceputScpTVA && (
                      <Typography variant="body2" color="text.secondary">
                        Inregistrat ca platitor TVA: {formatDate(company.dataInceputScpTVA)}
                      </Typography>
                    )}
                    {company.dataSfarsitScpTVA && (
                      <Typography variant="body2" color="text.secondary">
                        Inregistrarea TVA anulata: {formatDate(company.dataSfarsitScpTVA)}
                      </Typography>
                    )}
                    {company.dataInceputTvaInc && (
                      <Typography variant="body2" color="text.secondary">
                        TVA la incasare activat: {formatDate(company.dataInceputTvaInc)}
                      </Typography>
                    )}
                    {company.dataSfarsitTvaInc && (
                      <Typography variant="body2" color="text.secondary">
                        TVA la incasare dezactivat: {formatDate(company.dataSfarsitTvaInc)}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Split TVA */}
            {company.splitTVA && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Split TVA
                    </Typography>
                    <Chip
                      label="Split TVA activ"
                      color="warning"
                      size="small"
                    />
                    {company.dataInceputSplitTVA && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Inceput: {formatDate(company.dataInceputSplitTVA)}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Detalii sensibile */}
            <Grid size={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Detalii suplimentare
                    </Typography>
                    <Button
                      size="small"
                      startIcon={showSensitiveData ? <VisibilityOff /> : <Visibility />}
                      onClick={() => setShowSensitiveData(!showSensitiveData)}
                    >
                      {showSensitiveData ? 'Ascunde' : 'Afiseaza'} detalii sensibile
                    </Button>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        IBAN: <strong>{showSensitiveData ? (company.IBAN || '—') : '••••••••••••••••'}</strong>
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Telefon: <strong>{showSensitiveData ? (company.telefon || '—') : '••••••••••'}</strong>
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Fax: <strong>{showSensitiveData ? (company.fax || '—') : '••••••••••'}</strong>
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        Organ fiscal competent: <strong>{company.organFiscalCompetent || '—'}</strong>
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        Data inregistrarii: <strong>{formatDate(company.dataInregistrarii)}</strong>
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Ultima verificare ANAF: {formatDate(company.lastCheckedAt)} | 
                    Versiune API: {company.apiVersion} | 
                    Sursa: {company.source}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default CompanyCompactCard;
